import React, { useEffect, useMemo, useState } from "react";
import interiorParameterApi from "../services/interiorParameterApi";

const emptyDefinition = {
  param_key: "",
  label: "",
  description: "",
  data_type: "number",
  unit_id: "",
  group_id: "",
  default_value: "",
  allowed_scopes: ["project", "type", "instance", "binding"],
  enum_values: "",
  is_shared: 1,
  is_system: 0,
  active_flag: 1,
};

const emptyUnit = {
  code: "",
  name: "",
  unit_type: "",
  symbol: "",
  base_factor: 1,
  active_flag: 1,
};

const emptyGroup = {
  code: "",
  name: "",
  sort_order: 0,
  active_flag: 1,
};

function HeaderButton({ children, onClick, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 rounded border text-sm ${
        danger
          ? "text-red-600 bg-white hover:bg-red-50"
          : "bg-white hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-3 text-sm border-b-2 ${
        active
          ? "border-blue-600 text-blue-700 bg-blue-50 font-semibold"
          : "border-transparent text-slate-600 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function ScopeCheckboxes({ value = [], onChange }) {
  const scopes = ["project", "type", "instance", "binding"];

  function toggle(scope) {
    if (value.includes(scope)) {
      onChange(value.filter((x) => x !== scope));
    } else {
      onChange([...value, scope]);
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {scopes.map((scope) => (
        <label key={scope} className="flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={value.includes(scope)}
            onChange={() => toggle(scope)}
          />
          {scope}
        </label>
      ))}
    </div>
  );
}

export default function InteriorParameterRegistryPage() {
  const [activeTab, setActiveTab] = useState("definitions");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [definitions, setDefinitions] = useState([]);
  const [units, setUnits] = useState([]);
  const [groups, setGroups] = useState([]);

  const [editingDefinition, setEditingDefinition] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);

  async function load() {
    try {
      setLoading(true);

      const [defRes, unitRes, groupRes] = await Promise.all([
        interiorParameterApi.listDefinitions(),
        interiorParameterApi.listUnits(),
        interiorParameterApi.listGroups(),
      ]);

      setDefinitions(defRes.items || []);
      setUnits(unitRes.items || []);
      setGroups(groupRes.items || []);
    } catch (err) {
      console.error("load parameter registry error:", err);
      alert("Failed to load parameter registry.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filteredDefinitions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (definitions || []).filter((x) => {
      if (!q) return true;
      return (
        String(x.param_key || "").toLowerCase().includes(q) ||
        String(x.label || "").toLowerCase().includes(q) ||
        String(x.group_name || "").toLowerCase().includes(q)
      );
    });
  }, [definitions, search]);

  const filteredUnits = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (units || []).filter((x) => {
      if (!q) return true;
      return (
        String(x.code || "").toLowerCase().includes(q) ||
        String(x.name || "").toLowerCase().includes(q) ||
        String(x.symbol || "").toLowerCase().includes(q)
      );
    });
  }, [units, search]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (groups || []).filter((x) => {
      if (!q) return true;
      return (
        String(x.code || "").toLowerCase().includes(q) ||
        String(x.name || "").toLowerCase().includes(q)
      );
    });
  }, [groups, search]);

  function parseScopes(value) {
    try {
      if (Array.isArray(value)) return value;
      return JSON.parse(value || "[]");
    } catch {
      return [];
    }
  }

  async function saveDefinition() {
    const payload = {
      ...editingDefinition,
      unit_id: editingDefinition.unit_id || null,
      group_id: editingDefinition.group_id || null,
      allowed_scopes: JSON.stringify(editingDefinition.allowed_scopes || []),
    };

    if (editingDefinition.id) {
      await interiorParameterApi.updateDefinition(editingDefinition.id, payload);
    } else {
      await interiorParameterApi.createDefinition(payload);
    }

    setEditingDefinition(null);
    await load();
  }

  async function saveUnit() {
    if (editingUnit.id) {
      await interiorParameterApi.updateUnit(editingUnit.id, editingUnit);
    } else {
      await interiorParameterApi.createUnit(editingUnit);
    }

    setEditingUnit(null);
    await load();
  }

  async function saveGroup() {
    if (editingGroup.id) {
      await interiorParameterApi.updateGroup(editingGroup.id, editingGroup);
    } else {
      await interiorParameterApi.createGroup(editingGroup);
    }

    setEditingGroup(null);
    await load();
  }

  async function deactivateDefinition(id) {
    if (!window.confirm("Deactivate parameter definition?")) return;
    await interiorParameterApi.deleteDefinition(id);
    await load();
  }

  async function deactivateUnit(id) {
    if (!window.confirm("Deactivate unit?")) return;
    await interiorParameterApi.deleteUnit(id);
    await load();
  }

  async function deactivateGroup(id) {
    if (!window.confirm("Deactivate group?")) return;
    await interiorParameterApi.deleteGroup(id);
    await load();
  }

  return (
    <div className="h-[calc(100vh-80px)] bg-slate-100 flex flex-col overflow-hidden">
      <div className="h-16 bg-white border-b px-4 flex items-center justify-between">
        <div>
          <div className="text-xl font-semibold">Parameter Registry</div>
          <div className="text-xs text-slate-500">
            Shared parameters, units and parameter groups.
          </div>
        </div>

        <div className="flex gap-2">
          <HeaderButton onClick={load}>Refresh</HeaderButton>
          {activeTab === "definitions" && (
            <HeaderButton onClick={() => setEditingDefinition({ ...emptyDefinition })}>
              + Parameter
            </HeaderButton>
          )}
          {activeTab === "units" && (
            <HeaderButton onClick={() => setEditingUnit({ ...emptyUnit })}>
              + Unit
            </HeaderButton>
          )}
          {activeTab === "groups" && (
            <HeaderButton onClick={() => setEditingGroup({ ...emptyGroup })}>
              + Group
            </HeaderButton>
          )}
        </div>
      </div>

      <div className="bg-white border-b flex items-center justify-between">
        <div className="flex">
          <TabButton
            active={activeTab === "definitions"}
            onClick={() => setActiveTab("definitions")}
          >
            Parameter Definitions
          </TabButton>
          <TabButton active={activeTab === "units"} onClick={() => setActiveTab("units")}>
            Units
          </TabButton>
          <TabButton active={activeTab === "groups"} onClick={() => setActiveTab("groups")}>
            Groups
          </TabButton>
        </div>

        <div className="px-4">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="px-3 py-2 border rounded text-sm w-80"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4">
        {loading && <div className="text-slate-500">Loading...</div>}

        {activeTab === "definitions" && (
          <div className="bg-white border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="p-2 text-left">Key</th>
                  <th className="p-2 text-left">Label</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Unit</th>
                  <th className="p-2 text-left">Group</th>
                  <th className="p-2 text-left">Default</th>
                  <th className="p-2 text-left">Scopes</th>
                  <th className="p-2 text-left">Active</th>
                  <th className="p-2 text-left w-[180px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDefinitions.map((item) => (
                  <tr key={item.id} className="border-t">
                    <td className="p-2 font-mono">{item.param_key}</td>
                    <td className="p-2">{item.label}</td>
                    <td className="p-2">{item.data_type}</td>
                    <td className="p-2">{item.unit || "—"}</td>
                    <td className="p-2">{item.group_name || "—"}</td>
                    <td className="p-2 font-mono">{item.default_value ?? "—"}</td>
                    <td className="p-2 text-xs">{item.allowed_scopes}</td>
                    <td className="p-2">{item.active_flag === 0 ? "No" : "Yes"}</td>
                    <td className="p-2 flex gap-2">
                      <button
                        className="px-2 py-1 border rounded text-xs"
                        onClick={() =>
                          setEditingDefinition({
                            ...item,
                            unit_id: item.unit_id || "",
                            group_id: item.group_id || "",
                            allowed_scopes: parseScopes(item.allowed_scopes),
                          })
                        }
                      >
                        Edit
                      </button>
                      <button
                        className="px-2 py-1 border rounded text-xs text-red-600"
                        onClick={() => deactivateDefinition(item.id)}
                      >
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "units" && (
          <div className="bg-white border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-2 text-left">Code</th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Symbol</th>
                  <th className="p-2 text-left">Factor</th>
                  <th className="p-2 text-left">Active</th>
                  <th className="p-2 text-left w-[180px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUnits.map((item) => (
                  <tr key={item.id} className="border-t">
                    <td className="p-2 font-mono">{item.code}</td>
                    <td className="p-2">{item.name}</td>
                    <td className="p-2">{item.unit_type}</td>
                    <td className="p-2">{item.symbol}</td>
                    <td className="p-2">{item.base_factor}</td>
                    <td className="p-2">{item.active_flag === 0 ? "No" : "Yes"}</td>
                    <td className="p-2 flex gap-2">
                      <button
                        className="px-2 py-1 border rounded text-xs"
                        onClick={() => setEditingUnit({ ...item })}
                      >
                        Edit
                      </button>
                      <button
                        className="px-2 py-1 border rounded text-xs text-red-600"
                        onClick={() => deactivateUnit(item.id)}
                      >
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "groups" && (
          <div className="bg-white border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-2 text-left">Code</th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Sort</th>
                  <th className="p-2 text-left">Active</th>
                  <th className="p-2 text-left w-[180px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((item) => (
                  <tr key={item.id} className="border-t">
                    <td className="p-2 font-mono">{item.code}</td>
                    <td className="p-2">{item.name}</td>
                    <td className="p-2">{item.sort_order}</td>
                    <td className="p-2">{item.active_flag === 0 ? "No" : "Yes"}</td>
                    <td className="p-2 flex gap-2">
                      <button
                        className="px-2 py-1 border rounded text-xs"
                        onClick={() => setEditingGroup({ ...item })}
                      >
                        Edit
                      </button>
                      <button
                        className="px-2 py-1 border rounded text-xs text-red-600"
                        onClick={() => deactivateGroup(item.id)}
                      >
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingDefinition && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="w-[720px] bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="h-12 px-4 border-b bg-slate-50 flex items-center justify-between">
              <div className="font-semibold">
                {editingDefinition.id ? "Edit Parameter" : "Add Parameter"}
              </div>
              <button onClick={() => setEditingDefinition(null)} className="px-3 py-1 border rounded">
                Close
              </button>
            </div>

            <div className="p-4 grid grid-cols-2 gap-3">
              <input
                placeholder="key"
                value={editingDefinition.param_key}
                onChange={(e) =>
                  setEditingDefinition((p) => ({ ...p, param_key: e.target.value }))
                }
                className="px-3 py-2 border rounded font-mono"
              />
              <input
                placeholder="label"
                value={editingDefinition.label}
                onChange={(e) =>
                  setEditingDefinition((p) => ({ ...p, label: e.target.value }))
                }
                className="px-3 py-2 border rounded"
              />

              <select
                value={editingDefinition.data_type}
                onChange={(e) =>
                  setEditingDefinition((p) => ({ ...p, data_type: e.target.value }))
                }
                className="px-3 py-2 border rounded"
              >
                <option value="number">number</option>
                <option value="string">string</option>
                <option value="boolean">boolean</option>
                <option value="enum">enum</option>
                <option value="formula">formula</option>
                <option value="material">material</option>
              </select>

              <input
                placeholder="default value"
                value={editingDefinition.default_value ?? ""}
                onChange={(e) =>
                  setEditingDefinition((p) => ({
                    ...p,
                    default_value: e.target.value,
                  }))
                }
                className="px-3 py-2 border rounded"
              />

              <select
                value={editingDefinition.unit_id || ""}
                onChange={(e) =>
                  setEditingDefinition((p) => ({ ...p, unit_id: e.target.value }))
                }
                className="px-3 py-2 border rounded"
              >
                <option value="">No unit</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.symbol})
                  </option>
                ))}
              </select>

              <select
                value={editingDefinition.group_id || ""}
                onChange={(e) =>
                  setEditingDefinition((p) => ({ ...p, group_id: e.target.value }))
                }
                className="px-3 py-2 border rounded"
              >
                <option value="">No group</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>

              <textarea
                placeholder="description"
                value={editingDefinition.description || ""}
                onChange={(e) =>
                  setEditingDefinition((p) => ({ ...p, description: e.target.value }))
                }
                className="col-span-2 px-3 py-2 border rounded h-20"
              />

              <div className="col-span-2">
                <div className="text-xs text-slate-500 mb-1">Allowed scopes</div>
                <ScopeCheckboxes
                  value={editingDefinition.allowed_scopes || []}
                  onChange={(next) =>
                    setEditingDefinition((p) => ({ ...p, allowed_scopes: next }))
                  }
                />
              </div>
            </div>

            <div className="border-t bg-slate-50 px-4 py-3 flex justify-end gap-2">
              <button onClick={() => setEditingDefinition(null)} className="px-3 py-2 border rounded bg-white">
                Cancel
              </button>
              <button onClick={saveDefinition} className="px-4 py-2 rounded bg-blue-600 text-white">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {editingUnit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="w-[520px] bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="h-12 px-4 border-b bg-slate-50 flex items-center justify-between">
              <div className="font-semibold">{editingUnit.id ? "Edit Unit" : "Add Unit"}</div>
              <button onClick={() => setEditingUnit(null)} className="px-3 py-1 border rounded">
                Close
              </button>
            </div>

            <div className="p-4 grid grid-cols-2 gap-3">
              {["code", "name", "unit_type", "symbol", "base_factor"].map((key) => (
                <input
                  key={key}
                  placeholder={key}
                  value={editingUnit[key] ?? ""}
                  onChange={(e) =>
                    setEditingUnit((p) => ({ ...p, [key]: e.target.value }))
                  }
                  className="px-3 py-2 border rounded"
                />
              ))}
            </div>

            <div className="border-t bg-slate-50 px-4 py-3 flex justify-end gap-2">
              <button onClick={() => setEditingUnit(null)} className="px-3 py-2 border rounded bg-white">
                Cancel
              </button>
              <button onClick={saveUnit} className="px-4 py-2 rounded bg-blue-600 text-white">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {editingGroup && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="w-[520px] bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="h-12 px-4 border-b bg-slate-50 flex items-center justify-between">
              <div className="font-semibold">{editingGroup.id ? "Edit Group" : "Add Group"}</div>
              <button onClick={() => setEditingGroup(null)} className="px-3 py-1 border rounded">
                Close
              </button>
            </div>

            <div className="p-4 grid grid-cols-2 gap-3">
              {["code", "name", "sort_order"].map((key) => (
                <input
                  key={key}
                  placeholder={key}
                  value={editingGroup[key] ?? ""}
                  onChange={(e) =>
                    setEditingGroup((p) => ({ ...p, [key]: e.target.value }))
                  }
                  className="px-3 py-2 border rounded"
                />
              ))}
            </div>

            <div className="border-t bg-slate-50 px-4 py-3 flex justify-end gap-2">
              <button onClick={() => setEditingGroup(null)} className="px-3 py-2 border rounded bg-white">
                Cancel
              </button>
              <button onClick={saveGroup} className="px-4 py-2 rounded bg-blue-600 text-white">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}